import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { request } from '../utils';
import UserHeader from '../UserHeader';

function CricketTeamDetails(props) {
	const { user } = props;
	const { id: teamID } = useParams();
	const newsSentinelRef = useRef(null);
	const [state, setState] = useState({ 
		loading: true, 
		teamInfo: null, 
		players: [], 
		news: [],
		newsPagination: { limit: 3, offset: 0, hasMore: false, nextOffset: 0 },
		newsLoading: false,
		error: null 
	});
 
	useEffect(() => {
		const fetchTeamDetails = async () => {
			try {
				const res = await request({}, `/api/cricket/team/${teamID}`, 'GET', user?.token);
				const apiData = res?.data?.data || {};
				const teamInfo = Array.isArray(apiData.teamInfo) && apiData.teamInfo.length > 0 ? apiData.teamInfo[0] : null;
				const players = Array.isArray(apiData.playerInfo) ? apiData.playerInfo : [];

				setState((prev) => ({
					...prev,
					loading: false,
					teamInfo,
					players,
					error: null
				}));
			} catch (e) {
				setState((prev) => ({
					...prev,
					loading: false,
					teamInfo: null,
					players: [],
					news: [],
					newsPagination: { limit: 3, offset: 0, hasMore: false, nextOffset: 0 },
					newsLoading: false,
					error: 'Unable to load team data'
				}));
			}
		};
		fetchTeamDetails();
	}, [teamID, user?.token]);

	useEffect(() => {
		const fetchInitialNews = async () => {
			try {
				setState((prev) => ({ ...prev, newsLoading: true }));
				const res = await request({}, `/api/cricket/team/${teamID}/news?limit=3&offset=0`, 'GET', user?.token);
				const apiData = res?.data?.data || {};
				const news = Array.isArray(apiData.news) ? apiData.news : [];
				const pagination = apiData.pagination && typeof apiData.pagination === 'object' ? apiData.pagination : { limit: 3, offset: 0, hasMore: false, nextOffset: news.length };
				setState((prev) => ({
					...prev,
					news,
					newsPagination: pagination,
					newsLoading: false
				}));
			} catch (e) {
				setState((prev) => ({
					...prev,
					news: [],
					newsPagination: { limit: 3, offset: 0, hasMore: false, nextOffset: 0 },
					newsLoading: false
				}));
			}
		};
		fetchInitialNews();
	}, [teamID, user?.token]);

	useEffect(() => {
		const sentinel = newsSentinelRef.current;
		if (!sentinel) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry?.isIntersecting) return;
				setState((prev) => {
					if (prev.newsLoading || !prev.newsPagination?.hasMore) return prev;
					return { ...prev, newsLoading: true };
				});
			},
			{ root: null, rootMargin: '250px', threshold: 0 }
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [state.news.length]);

	useEffect(() => {
		const onScroll = () => {
			const sentinel = newsSentinelRef.current;
			if (!sentinel) return;
			const rect = sentinel.getBoundingClientRect();
			const nearViewport = rect.top <= window.innerHeight + 250;
			if (!nearViewport) return;
			setState((prev) => {
				if (prev.newsLoading || !prev.newsPagination?.hasMore) return prev;
				return { ...prev, newsLoading: true };
			});
		};
		window.addEventListener('scroll', onScroll);
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		const loadMore = async () => {
			if (!state.newsLoading) return;
			const { newsPagination } = state;
			if (!newsPagination?.hasMore) {
				setState((prev) => ({ ...prev, newsLoading: false }));
				return;
			}
			try {
				const nextOffset = Number.isFinite(Number(newsPagination.nextOffset)) ? Number(newsPagination.nextOffset) : 0;
				const limit = Number.isFinite(Number(newsPagination.limit)) ? Number(newsPagination.limit) : 3;
				const res = await request({}, `/api/cricket/team/${teamID}/news?limit=${limit}&offset=${nextOffset}`, 'GET', user?.token);
				const apiData = res?.data?.data || {};
				const nextNews = Array.isArray(apiData.news) ? apiData.news : [];
				const nextPagination = apiData.pagination && typeof apiData.pagination === 'object' ? apiData.pagination : { limit, offset: nextOffset, hasMore: false, nextOffset: nextOffset + nextNews.length };
				setState((prev) => ({
					...prev,
					news: Array.isArray(prev.news) ? [...prev.news, ...nextNews] : nextNews,
					newsPagination: nextPagination,
					newsLoading: false
				}));
			} catch (e) {
				setState((prev) => ({ ...prev, newsLoading: false, newsPagination: { ...(prev.newsPagination || {}), hasMore: false } }));
			}
		};
		loadMore();
	}, [state.newsLoading, state.newsPagination, teamID, user?.token]);

	if (state.loading) {
		return (
			<div id='fullHeight'>
				<div id='loading' />
			</div>
		);
	}

	if (state.error || !state.teamInfo) {
		return (
			<div className='gameMainDiv'>
				<UserHeader backgroundColor={'#f7f7f7'} title='Team Details' />
				<div className='container' style={{ padding: '50px', textAlign: 'center' }}>
					<h2>Team Not Found</h2>
					<p>Unable to load team information. Please try again later.</p>
				</div>
			</div>
		);
	}

	const { teamInfo, players, news } = state;
	const upcomingMatches = [
		{
			id: 'static-1',
			away: { shortName: 'PBKS', logoUrl: '' },
			home: { shortName: 'RCB', logoUrl: '' }
		},
		{
			id: 'static-2',
			away: { shortName: 'MI', logoUrl: '' },
			home: { shortName: 'PBKS', logoUrl: '' }
		},
		{
			id: 'static-3',
			away: { shortName: 'MI', logoUrl: '' },
			home: { shortName: 'GT', logoUrl: '' }
		}
	];

	const renderTeamBadge = (team) => {
		const shortName = team?.shortName || '';
		const logoUrl = team?.logoUrl || '';
		return (
			<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
				<div
					style={{
						width: '56px',
						height: '56px',
						borderRadius: '50%',
						backgroundColor: '#fff',
						border: '1px solid rgba(0,0,0,0.12)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						overflow: 'hidden'
					}}
				>
					{logoUrl ? (
						<img src={logoUrl} alt={shortName} style={{ width: '44px', height: '44px', objectFit: 'contain' }} />
					) : (
						<span style={{ fontWeight: 700, color: '#c00' }}>{shortName.slice(0, 3).toUpperCase()}</span>
					)}
				</div>
				<div style={{ fontSize: '12px', fontWeight: 700, color: '#111' }}>{shortName}</div>
			</div>
		);
	};

	return (
		<section className='gameMainSection'>
			<div className='gameMainDiv'>
				<UserHeader backgroundColor={'#f7f7f7'} title='Team Details' />
				<div className='gameBoxShadow'>
					{/* Team Header */}
					<div style={{ 
						background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
						color: 'white', 
						padding: '30px', 
						textAlign: 'center',
						marginBottom: '30px'
					}}>
						{teamInfo.logo_url && (
							<img 
								src={teamInfo.logo_url} 
								alt={teamInfo.team_name}
								style={{ 
									width: '80px', 
									height: '80px', 
									objectFit: 'contain',
									marginBottom: '15px',
									background: 'white',
									padding: '10px',
									borderRadius: '10px'
								}}
							/>
						)}
						<h1 style={{ margin: 0, fontSize: '2.5rem', fontWeight: 'bold' }}>
							{teamInfo.team_name}
						</h1>						
					</div>

					{/* Team Stats */}
					<div style={{ padding: '0 30px' }}>
						<div className='row'>
							<div className='col-md-6'>
								<div className='player-stats' style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '10px', marginBottom: '20px' }}>
									<h4 style={{ color: '#333', marginBottom: '15px' }}>Team Information</h4>
									{teamInfo.team_name && (
										<p><strong>Team Name:</strong> {teamInfo.team_name}</p>
									)}
									{teamInfo.league && (
										<p><strong>League:</strong> {teamInfo.league}</p>
									)}
									{teamInfo.team_code && (
										<p><strong>Team Code:</strong> {teamInfo.team_code}</p>
									)}
									{teamInfo.team_short_name && (
										<p><strong>Short Name:</strong> {teamInfo.team_short_name}</p>
									)}
									{teamInfo.team_id && (
										<p><strong>Team ID:</strong> {teamInfo.team_id}</p>
									)}
								</div>
							</div>
							<div className='col-md-6'>
								<div className='player-stats' style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '10px', marginBottom: '20px' }}>
									<h4 style={{ color: '#333', marginBottom: '15px' }}>Upcoming Matches</h4>
									<div style={{ display: 'flex', gap: '14px', overflowX: 'auto', paddingBottom: '4px' }}>
										{upcomingMatches.map((m) => (
											<div
												key={m.id}
												style={{
													minWidth: '180px',
													borderRadius: '10px',
													border: '1px solid rgba(0,0,0,0.12)',
													overflow: 'hidden',
													backgroundColor: '#fff'
												}}
											>
												<div style={{ backgroundColor: '#1aa36a', color: '#fff', padding: '8px 10px', fontSize: '12px', fontWeight: 700 }}>
													Upcoming
												</div>
												<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '12px' }}>
													<div style={{ textAlign: 'center' }}>
														{renderTeamBadge(m.away)}
													</div>
													<div style={{ textAlign: 'center' }}>
														{renderTeamBadge(m.home)}
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>

						{/* Players Section */}
						{players.length > 0 && (
							<div className='player-stats' style={{ marginTop: '30px' }}>
								<h3 style={{ color: '#333', marginBottom: '20px' }}>Team Squad ({players.length} players)</h3>
								<div className='row'>
									{players.map((player, index) => (
										<div key={player.player_id || index} className='col-md-6 col-lg-4' style={{ marginBottom: '20px' }}>
											<div style={{
												background: 'white',
												border: '1px solid #e0e0e0',
												borderRadius: '8px',
												padding: '15px',
												boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
											}}>
												<h5 style={{ margin: '0 0 10px 0', color: '#333' }}>
													{player.nickname || player.player_name}
												</h5>
												<p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
													<strong>Position:</strong> {player.position || player.position_category || 'N/A'}
												</p>
												<p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
													<strong>Health Score:</strong> {player.gaming_score || '10'}
												</p>
												{player.injury_status && (
													<p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
														<strong>Status:</strong> {player.injury_status}
													</p>
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{/* News Section */}
						{news.length > 0 && (
							<div className='player-stats' style={{ marginTop: '30px' }}>
								<h3 style={{ color: '#333', marginBottom: '20px' }}>Latest Team News</h3>
								{news.map((item, index) => (
									<div key={item.newsId || index} style={{
										background: 'white',
										border: '1px solid #e0e0e0',
										borderRadius: '8px',
										padding: '20px',
										marginBottom: '15px',
										boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
									}}>
										{item.Updated && (
											<p style={{ 
												fontSize: '12px', 
												color: '#777', 
												marginBottom: '8px' 
											}}>
												{new Date(item.Updated).toLocaleDateString()}
											</p>
										)}
										<h5 style={{ 
											margin: '0 0 10px 0', 
											color: '#333', 
											fontWeight: 'bold' 
										}}>
											{item.Title || item.headline || 'Team News Update'}
										</h5>
										{item.Content && (
											<p style={{
												margin: 0,
												color: '#666',
												lineHeight: '1.5'
											}}>
												{item.Content}
											</p>
										)}
									</div>
								))}
								<div ref={newsSentinelRef} style={{ height: '1px' }} />
							</div>
						)}
					</div>
				</div>
			</div>
	</section>
	);

}

const mapStateToProps = (state) => ({
	user: state.user
});

export default connect(mapStateToProps)(CricketTeamDetails);
